import React from 'react'

// ─────────────────────────────────────────────────────────────────────────────
// Pills / tags / chips
//
//   <Chip tone="orange|green|amber|red|blue|neutral">Label</Chip>
//   <StatusTag variant="pflicht|answered|deactivated|new|conditional|modified" />
//   <CategoryChip color="#e85d1f" softColor="#fff1e8">Empfehlungen</CategoryChip>
//
// All chips are inline-flex pills. Use `icon` to prepend an icon component.
// ─────────────────────────────────────────────────────────────────────────────

// Each tone is built like a miniature card-button: white-ish surface, hairline
// tinted border, soft tone-matched shadow (same elevation language as the
// RowCard "active" variants), plus a slight hover lift so they feel pressable.
const TONES = {
  neutral: 'bg-white text-[#6b6862] border-[#e7e5df] shadow-[0_1px_2px_rgba(20,20,16,.04)] hover:bg-[#f5f4f0] hover:border-[#d6d3cb]',
  orange:  'bg-[#fff1e8] text-[#7a2b08] border-[rgba(232,93,31,0.30)] shadow-[0_1px_2px_rgba(232,93,31,.04),0_4px_10px_-8px_rgba(232,93,31,.40)] hover:bg-[#ffe2cf] hover:border-[rgba(232,93,31,0.45)]',
  amber:   'bg-[#fbf1e1] text-[#8a5410] border-[rgba(184,117,20,0.30)] shadow-[0_1px_2px_rgba(184,117,20,.04),0_4px_10px_-8px_rgba(184,117,20,.35)] hover:bg-[#f6e4c3] hover:border-[rgba(184,117,20,0.45)]',
  green:   'bg-[#e7f3eb] text-[#1f5a37] border-[rgba(47,125,79,0.30)] shadow-[0_1px_2px_rgba(47,125,79,.04),0_4px_10px_-8px_rgba(47,125,79,.35)] hover:bg-[#d6ead9] hover:border-[rgba(47,125,79,0.45)]',
  red:     'bg-[#fbeae6] text-[#b3321c] border-[rgba(179,50,28,0.25)] shadow-[0_1px_2px_rgba(179,50,28,.04),0_4px_10px_-8px_rgba(179,50,28,.35)] hover:bg-[#f6d5cd] hover:border-[rgba(179,50,28,0.45)]',
  blue:    'bg-[#eef4fb] text-[#1f5fa8] border-[rgba(31,95,168,0.25)] shadow-[0_1px_2px_rgba(31,95,168,.04),0_4px_10px_-8px_rgba(31,95,168,.35)] hover:bg-[#dbe7f5] hover:border-[rgba(31,95,168,0.45)]',
  purple:  'bg-[#f3edff] text-[#5b2dc4] border-[rgba(124,58,237,0.25)] shadow-[0_1px_2px_rgba(124,58,237,.04),0_4px_10px_-8px_rgba(124,58,237,.35)] hover:bg-[#e7daff] hover:border-[rgba(124,58,237,0.45)]',
  // Legacy alias , kept so existing call-sites that say tone="greenSoft" still work.
  greenSoft: 'bg-[#e7f3eb] text-[#1f5a37] border-[rgba(47,125,79,0.30)] shadow-[0_1px_2px_rgba(47,125,79,.04),0_4px_10px_-8px_rgba(47,125,79,.35)]',
}

export function Chip({
  tone = 'neutral',
  icon: Icon,
  iconSize = 10,
  uppercase = true,
  size = 'sm',
  className = '',
  children,
}) {
  const palette = TONES[tone] || TONES.neutral
  const dims =
    size === 'md'
      ? 'px-2.5 py-0.5 text-[11px]'
      : 'px-2 py-0.5 text-[10px]'
  const caps = uppercase ? 'uppercase tracking-[0.05em]' : ''
  return (
    <span
      className={
        'inline-flex items-center gap-1 rounded-[8px] border font-bold transition-all ' +
        dims + ' ' + caps + ' ' + palette + (className ? ' ' + className : '')
      }
    >
      {Icon && <Icon size={iconSize}/>}
      {children}
    </span>
  )
}

// CategoryChip , neutral surface pill identified by a small color dot.
//
// Uses the same chrome as the rest of the design system (surface-2 bg,
// hairline line border, ink-2 label). The dot is the ONLY part that
// carries the category color, so a list of chips reads as one consistent
// group instead of five clashing badges.
//
//   <CategoryChip color="#e85d1f">Empfehlungen</CategoryChip>
//   <CategoryChip color="#2f7d4f" active>Qualitätsindikatoren</CategoryChip>
//
// The `active` prop switches to the peach surface (used in the Add-question
// modal's category strip and the Pflichtkapitel selection rows).
export function CategoryChip({ color, active = false, className = '', children }) {
  const palette = active
    ? 'bg-[#fff1e8] border-[rgba(232,93,31,0.30)] text-[#1a1816] font-semibold ' +
      'shadow-[0_1px_2px_rgba(232,93,31,.04),0_4px_10px_-8px_rgba(232,93,31,.40)] ' +
      'hover:bg-[#ffe2cf] hover:border-[rgba(232,93,31,0.45)]'
    : 'bg-white border-[#e7e5df] text-[#3a3733] font-medium ' +
      'shadow-[0_1px_2px_rgba(20,20,16,.04)] ' +
      'hover:bg-[#f5f4f0] hover:border-[#d6d3cb]'
  return (
    <span
      className={
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[8px] text-[12px] border transition-all ' +
        palette + (className ? ' ' + className : '')
      }
    >
      <span className="w-[7px] h-[7px] rounded-full flex-shrink-0" style={{ background: color }}/>
      {children}
    </span>
  )
}

// Common status tags , pre-baked variants for the design system.
const STATUS_VARIANTS = {
  pflicht:     { tone: 'orange',  caps: true,  shape: 'rounded', text: 'Pflicht' },
  answered:    { tone: 'green',   caps: true,  shape: 'pill',    text: 'Beantwortet' },
  deactivated: { tone: 'neutral', caps: true,  shape: 'pill',    text: 'Deaktiviert' },
  new:         { tone: 'orange',  caps: true,  shape: 'pill',    text: 'Neu' },
  modified:    { tone: 'amber',   caps: true,  shape: 'pill',    text: 'Modifiziert' },
}

export function StatusTag({ variant, label, icon, className = '' }) {
  const v = STATUS_VARIANTS[variant] || { tone: 'neutral', caps: true, text: label || variant }
  const palette = TONES[v.tone]
  return (
    <span
      className={
        'inline-flex items-center gap-1 px-2 py-0.5 border font-bold text-[10px] rounded-[8px] transition-all ' +
        (v.caps ? 'uppercase tracking-[0.05em] ' : '') +
        palette + (className ? ' ' + className : '')
      }
    >
      {icon && React.createElement(icon, { size: 10 })}
      {label || v.text}
    </span>
  )
}
